// src/components/tokenPage/derivatives/WeightedFundingChartCard.tsx

import React, { useState } from "react";
import TimeRangePicker, { type TimeRangeOption } from "@/components/common/TimeRangePicker";
import TokenWeightedFundingRateChart from "@/components/tokenPage/derivatives/TokenWeightedFundingRateChart";
import { useTokenDerivatives } from "@/contexts/token";
import { DataCard } from "@/components/ui";

interface Props {
  ticker: string;
}

const WeightedFundingChartCard: React.FC<Props> = ({ ticker }) => {
  const chartRangeOptions: readonly TimeRangeOption[] = ['30d','90d','180d','365d','All'];
  const [wfrRange, setWfrRange] = useState<TimeRangeOption>('180d');
  const [weightedTab, setWeightedTab] = useState<'oiw'|'volw'>('oiw');
  const toDays = (r: TimeRangeOption): number | null => r === 'All' ? null : Number(r.replace('d',''));

  // Use context to check data availability (chart fetches its own data for aggregation)
  const { fundingOiWeighted, fundingVolWeighted, isLoading } = useTokenDerivatives();
  const hasFundingData = fundingOiWeighted.series.length > 0 || fundingVolWeighted.series.length > 0;

  // Hide if no data available (after loading)
  if (!isLoading && !hasFundingData) {
    return null;
  }

  return (
    <DataCard
      title="Weighted Funding Rate"
      subtitle="Funding rate vs price"
      icon="split-square-horizontal"
      iconColor="text-purple-400"
      isLoading={isLoading}
      loadingVariant="chart"
      actions={
        <div className="flex items-center gap-2">
          <div className="flex bg-gray-800/50 rounded-lg p-0.5 border border-gray-700/50">
            {(['oiw','volw'] as const).map(t => (
              <button key={t} className={`px-2.5 py-1 rounded-md text-[12px] font-medium transition-all ${weightedTab===t ? 'bg-slate-600/30 text-slate-200' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/30'}`} onClick={() => setWeightedTab(t)}>
                {t === 'oiw' ? 'OI‑weighted' : 'Vol‑weighted'}
              </button>
            ))}
          </div>
          <TimeRangePicker options={chartRangeOptions} value={wfrRange} onChange={setWfrRange} theme="blue" />
        </div>
      }
    >
      {weightedTab === 'oiw' ? (
        <TokenWeightedFundingRateChart dataType="oiw" tokenId={ticker} hideTitle containerClassName="h-[420px] w-full" externalRangeDays={toDays(wfrRange)} />
      ) : (
        <TokenWeightedFundingRateChart dataType="volw" tokenId={ticker} hideTitle containerClassName="h-[420px] w-full" externalRangeDays={toDays(wfrRange)} />
      )}
    </DataCard>
  );
};

export default WeightedFundingChartCard;
